import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  Leaf, CreditCard, CheckCircle, AlertCircle, DollarSign,
  FileText, Clock, Heart, Loader
} from 'lucide-react';
import './InvoicePayment.css';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

function InvoicePayment() {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tipAmount, setTipAmount] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);

  // Fetch invoice data on mount
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const response = await fetch('/.netlify/functions/invoice-pay', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getInvoice', paymentToken: token })
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          setError(result.error || 'Unable to load invoice');
          return;
        }

        setInvoice(result.invoice);

        // Default tip to 20%
        if (!result.invoice.isPaid) {
          const amount = result.invoice.balance || result.invoice.total;
          setTipAmount(Math.round(amount * 0.20 * 100) / 100);
        }
      } catch (err) {
        console.error('Error fetching invoice:', err);
        setError('Unable to load invoice. Please check your link and try again.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchInvoice();
    } else {
      setError('Invalid payment link');
      setLoading(false);
    }
  }, [token]);

  const handlePay = async () => {
    if (!invoice || paying) return;

    setPaying(true);
    setPayError(null);

    try {
      const finalTip = tipAmount === null ? 0 : tipAmount;
      const tipCents = Math.round(finalTip * 100);

      const response = await fetch('/.netlify/functions/invoice-pay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'createPayment',
          paymentToken: token,
          tipAmountCents: tipCents
        })
      });

      const result = await response.json();

      if (result.success && result.hostedUrl) {
        // Redirect to Stripe payment page
        window.location.href = result.hostedUrl;
      } else {
        setPayError(result.error || 'Failed to create payment. Please try again.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setPayError('Something went wrong. Please try again or contact us.');
    } finally {
      setPaying(false);
    }
  };

  // For deposit invoices where deposit hasn't been paid yet, show only the deposit amount
  const isDepositDue = invoice && invoice.isDeposit && !invoice.depositPaid && invoice.depositAmount > 0;
  const invoiceAmount = invoice
    ? (isDepositDue ? invoice.depositAmount : (invoice.balance || invoice.total))
    : 0;
  const effectiveTip = tipAmount === null ? 0 : tipAmount;
  const grandTotal = invoiceAmount + effectiveTip;

  // Loading state
  if (loading) {
    return (
      <div className="invoice-pay-page">
        <div className="invoice-pay-container">
          <div className="invoice-pay-header">
            <div className="invoice-pay-logo">
              <Leaf size={28} />
              <div>
                <h1>Two Sisters</h1>
                <span>Cleaning & Organizing</span>
              </div>
            </div>
          </div>
          <div className="invoice-pay-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <Loader size={32} className="spin" style={{ color: '#2c5f4e', margin: '0 auto 16px' }} />
            <p style={{ color: '#64748b' }}>Loading your invoice...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="invoice-pay-page">
        <div className="invoice-pay-container">
          <div className="invoice-pay-header">
            <div className="invoice-pay-logo">
              <Leaf size={28} />
              <div>
                <h1>Two Sisters</h1>
                <span>Cleaning & Organizing</span>
              </div>
            </div>
          </div>
          <div className="invoice-pay-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <AlertCircle size={48} style={{ color: '#dc2626', margin: '0 auto 16px' }} />
            <h2 style={{ color: '#1a2634', marginBottom: 8 }}>Unable to Load Invoice</h2>
            <p style={{ color: '#64748b' }}>{error}</p>
            <p style={{ color: '#94a3b8', fontSize: 14, marginTop: 16 }}>
              If you believe this is an error, please contact us at{' '}
              <a href="mailto:hello@twosisterscleaningco.com" style={{ color: '#2c5f4e' }}>
                hello@twosisterscleaningco.com
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Already paid
  if (invoice.isPaid) {
    return (
      <div className="invoice-pay-page">
        <div className="invoice-pay-container">
          <div className="invoice-pay-header">
            <div className="invoice-pay-logo">
              <Leaf size={28} />
              <div>
                <h1>Two Sisters</h1>
                <span>Cleaning & Organizing</span>
              </div>
            </div>
          </div>
          <div className="invoice-pay-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <CheckCircle size={48} style={{ color: '#16a34a', margin: '0 auto 16px' }} />
            <h2 style={{ color: '#1a2634', marginBottom: 8 }}>Invoice Paid</h2>
            <p style={{ color: '#64748b' }}>
              Invoice #{invoice.invoiceNumber} has already been paid.
              {invoice.paidAt && ` Payment received on ${format(parseISO(invoice.paidAt), 'MMMM d, yyyy')}.`}
            </p>
            <p style={{ color: '#16a34a', fontWeight: 600, fontSize: 20, marginTop: 16 }}>
              {formatCurrency(invoice.total)}
            </p>
            <p style={{ color: '#94a3b8', fontSize: 14, marginTop: 24 }}>
              Thank you for your payment!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-pay-page">
      <div className="invoice-pay-container">
        {/* Header */}
        <div className="invoice-pay-header">
          <div className="invoice-pay-logo">
            <Leaf size={28} />
            <div>
              <h1>Two Sisters</h1>
              <span>Cleaning & Organizing</span>
            </div>
          </div>
        </div>

        {/* Invoice Details Card */}
        <div className="invoice-pay-card">
          <div className="invoice-pay-title">
            <FileText size={20} />
            <h2>Invoice #{invoice.invoiceNumber}</h2>
          </div>

          {invoice.clientName && (
            <p className="invoice-pay-client">{invoice.clientName}</p>
          )}

          {invoice.propertyAddress && (
            <p className="invoice-pay-address">{invoice.propertyAddress}</p>
          )}

          {/* Line Items */}
          {invoice.lineItems && invoice.lineItems.length > 0 && (
            <div className="invoice-pay-items">
              {invoice.lineItems.map((item, i) => (
                <div key={i} className="invoice-pay-item">
                  <span className="item-desc">{item.description}</span>
                  <span className="item-amount">{formatCurrency(item.total)}</span>
                </div>
              ))}
            </div>
          )}

          <div className="invoice-pay-total-row">
            <span>{isDepositDue ? 'Deposit Due (50%)' : 'Invoice Total'}</span>
            <span className="invoice-pay-amount">{formatCurrency(invoiceAmount)}</span>
          </div>

          {isDepositDue && (
            <div className="invoice-pay-due" style={{ marginTop: 4, color: '#64748b', fontSize: 13 }}>
              <span>Full service total: {formatCurrency(invoice.total)} — remaining balance of {formatCurrency(invoice.total - invoice.depositAmount)} due upon completion</span>
            </div>
          )}

          {invoice.dueDate && (
            <div className="invoice-pay-due">
              <Clock size={14} />
              <span>Due {format(parseISO(invoice.dueDate), 'MMMM d, yyyy')}</span>
            </div>
          )}
        </div>

        {/* Tip Section */}
        <div className="invoice-pay-card">
          <div className="invoice-pay-title">
            <Heart size={20} />
            <h2>Add a Tip?</h2>
          </div>
          <p className="tip-subtitle">Tips go directly to your cleaning team and are always appreciated!</p>

          <div className="tip-options">
            {[
              { label: '15%', value: Math.round(invoiceAmount * 0.15 * 100) / 100 },
              { label: '20%', value: Math.round(invoiceAmount * 0.20 * 100) / 100 },
              { label: '25%', value: Math.round(invoiceAmount * 0.25 * 100) / 100 },
              { label: 'No tip', value: 0 }
            ].map(opt => (
              <button
                key={opt.label}
                className={`tip-option ${tipAmount === opt.value && customTip === '' ? 'selected' : ''}`}
                onClick={() => { setTipAmount(opt.value); setCustomTip(''); }}
              >
                <span className="tip-label">{opt.label}</span>
                {opt.value > 0 && <span className="tip-value">{formatCurrency(opt.value)}</span>}
              </button>
            ))}
          </div>

          <div className="tip-custom">
            <label>Custom amount</label>
            <div className="tip-custom-input">
              <DollarSign size={16} />
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={customTip}
                onChange={(e) => {
                  setCustomTip(e.target.value);
                  const val = parseFloat(e.target.value);
                  setTipAmount(isNaN(val) ? 0 : Math.max(0, val));
                }}
              />
            </div>
          </div>
        </div>

        {/* Payment Summary */}
        <div className="invoice-pay-card summary">
          <div className="summary-row">
            <span>Invoice</span>
            <span>{formatCurrency(invoiceAmount)}</span>
          </div>
          {effectiveTip > 0 && (
            <div className="summary-row tip">
              <span>Tip</span>
              <span>{formatCurrency(effectiveTip)}</span>
            </div>
          )}
          <div className="summary-row total">
            <span>Total</span>
            <span>{formatCurrency(grandTotal)}</span>
          </div>

          {payError && (
            <div className="pay-error">
              <AlertCircle size={16} />
              <span>{payError}</span>
            </div>
          )}

          <button
            className="btn-pay-now"
            onClick={handlePay}
            disabled={paying}
          >
            {paying ? (
              <>
                <Loader size={18} className="spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard size={18} />
                Pay {formatCurrency(grandTotal)}
              </>
            )}
          </button>

          <p className="pay-secure">
            Secure payment powered by Stripe
          </p>
        </div>

        {/* Footer */}
        <div className="invoice-pay-footer">
          <p>Two Sisters Cleaning & Organizing</p>
          <p>Questions? <a href="mailto:hello@twosisterscleaningco.com">hello@twosisterscleaningco.com</a></p>
          <p className="pay-terms-note">
            By submitting payment, you agree to our{' '}
            <a href="https://keelgroup.app/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default InvoicePayment;
